
import {
  Button,
  Modal,
  SplitButton,
  ValidationProvider,
  useValidation
} from "@etframework/ui.components";
import moment from "moment";
import { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { AgencyCombobox } from "../../components/CreateTaskModalComponents/AgencyComboBox";
import { AssigneeCheckBox } from "../../components/CreateTaskModalComponents/AssigneeCheckBox";
import { AssigneeDropDown } from "../../components/CreateTaskModalComponents/AssigneeDropDown";
import { AutoRolloverContainer } from "../../components/CreateTaskModalComponents/AutoRolloverContainer";
import { ClientDropDown } from "../../components/CreateTaskModalComponents/ClientDropDown";
import { CollaboratorMultiSelect } from "../../components/CreateTaskModalComponents/CollaboratorMultiSelect";
import { DueDatePicker } from "../../components/CreateTaskModalComponents/DueDatePicker";
import { DueDateRule } from "../../components/CreateTaskModalComponents/DueDateRule";
import { SelectedTemplate } from "../../components/CreateTaskModalComponents/SelectedTemplate";
import { TagMultiSelect } from "../../components/CreateTaskModalComponents/TagMultiSelect";
import { TaskBoardDropDown } from "../../components/CreateTaskModalComponents/TaskBoardDropDown";
import { TaskCustomFields } from "../../components/CreateTaskModalComponents/TaskCustomFields";
import { TaskDescriptionTextBox } from "../../components/CreateTaskModalComponents/TaskDescriptionTextBox";
import { TaskTemplateDropDown } from "../../components/CreateTaskModalComponents/TaskTemplateDropDown";
import { TaskTypeDropDown } from "../../components/CreateTaskModalComponents/TaskTypeDropDown";
import { TaxYearDropDown } from "../../components/CreateTaskModalComponents/TaxYearDropDown";
import { StageDropdown } from "../../components/OverlayComponents/StageDropdown";
import { useBoardProvider } from "../../contexts/board/boardContext";
import taskService from "../../data/api/taskService";
import { useCreateTask } from "../../data/mutations/taskMutations";
import { useAgencies } from "../../data/queries/agencyQueries";
import { useBoardPermissions } from "../../data/queries/boardQueries";
import { useTaxYears } from "../../data/queries/taxReturnQueries";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import useModalEvent from "../../hooks/useModalEvent";
import Enums from "../../types/enums";
import { isGuid, isTruthyString } from "../../utils/helpers";

export const CreateTaskModal = () => {
  return (
    <ValidationProvider>
      <InnerCreateTaskModal />
    </ValidationProvider>
  );
};

const InnerCreateTaskModal = () => {
  const createAndOpenBtn = sessionStorage.getItem("create-and-open");
  const { currentBoardId } = useBoardProvider();
  const [persistentData, setPersistentData] = useState({});
  const { setValidationErrors, clearValidationErrors } = useValidation();
  const { t } = useTranslation(["CreateTaskModal"], {
    useSuspense: false
  });
  const { Events, toggleEvent } = useEvent();
  const { UserId } = useDueDatesAuth();

  const boardRef = useRef(null);
  const taskTypeRef = useRef(null);
  const templateRef = useRef(null);
  const dueDateRuleRef = useRef(null);
  const taxYearRef = useRef(null);
  const clientRef = useRef(null);
  const stageRef = useRef(null);
  const assigneeRef = useRef(null);
  const tagsRef = useRef(null);
  const collaboratorRef = useRef(null);
  const descriptionRef = useRef(null);
  const dueDateRef = useRef(null);
  const startDateRef = useRef(null);
  const periodEndRef = useRef(null);
  const taxDeadlineRef = useRef(null);
  const assigneeCheckBoxRef = useRef(null);
  const autoRolloverComponentRef = useRef(null);
  const selectedTemplateRef = useRef(null);
  const taskCustomFieldsRef = useRef(null);
  const agencyRef = useRef(null);
  const createSplitButtonRef = useRef(null);
  const ongoingCalculateRequest = useRef(0);

  const { data: boardPermissions } = useBoardPermissions(currentBoardId);
  const { data: taxYears } = useTaxYears();
  const { data: agencies } = useAgencies();

  const { mutate: createTask, isPending } = useCreateTask();
  const { calculateDueDate } = taskService();

  //Modal
  const modalRef = useRef();
  const { ModalEvents, useRegisterEvent } = useModalEvent();
  const [showModal, setShowModal] = useState(false);

  const id = ModalEvents.CreateTaskModal.Id;
  const ids = {
    board: id + "_board-dropdown",
    taskType: id + "_task-type-dropdown",
    template: id + "_template-dropdown",
    name: id + "_task-name-input",
    taxYear: id + "_tax-year-dropdown",
    dueDate: id + "_due-date-input",
    startDate: id + "start-date-input",
    periodEnd: id + "_period-end-input",
    taxDeadline: id + "_tax-deadline-input",
    client: id + "_client-dropdown",
    assignee: id + "_assignee-dropdown",
    stage: id + "_stage-dropdown",
    tags: id + "_tags-input",
    createButton: id + "_create-button",
    description: id + "_description-input",
    dueDateRule: id + "_due-date-rule",
    assignToMe: id + "_assign-to-me-checkbox",
    collaborators: id + "_collaborators-dropdown",
    autoRollover: id + "_auto-rollover-switch",
    agency: id + "_agency-input"
  };

  const headerTemplate = () => {
    return (
      <div className="d-flex align-items-center gap-3">
        <div className="d-flex align-items-center">
          <span>{t("NewTasksFor")}</span>
          <div className="board-dd">
            <TaskBoardDropDown
              id={ids.board}
              initialValue={persistentData?.BoardId ?? currentBoardId}
              change={onTaskBoardChange}
              ref={boardRef}
            />
          </div>
        </div>
        <div className="task-type-dd">
          <TaskTypeDropDown
            id={ids.taskType}
            change={onTaskTypeChange}
            ref={taskTypeRef}
            currentBoardId={persistentData?.BoardId ?? currentBoardId}
            initialValue={persistentData?.TaskTypeId}
          />
        </div>
      </div>
    );
  };

  const formatAgency = (value) => {
    if (!value?.Name) return null;

    if (value?.Id === value?.Name) return { Name: value?.Name };
    else return { Id: value?.Id, Name: value?.Name };
  };

  const handleClearTemplate = () => {
    dueDateRef.current?.setHasDueDateInfo(false);
    dueDateRuleRef.current?.setDueDateRuleInfo(null);
    taxDeadlineRef.current?.setHasDueDateInfo(false);
    periodEndRef.current?.setHasDueDateInfo(false);
    startDateRef.current?.setHasDueDateInfo(false);

    descriptionRef.current.setValue("");
    tagsRef.current.setValue([]);
    dueDateRef.current?.setCalculatedDate(null);
    taxDeadlineRef.current?.setCalculatedDate(null);
    periodEndRef.current?.setCalculatedDate(null);
    startDateRef.current?.setCalculatedDate(null);
    selectedTemplateRef.current?.setCurrentTemplate(null);
    templateRef.current?.setValue(null);
  };

  const onTaskBoardChange = async (board) => {
    taskTypeRef.current?.setBoardId(board.Id);
    onTaskTypeChange(board.TasksTypes?.[0]);

    stageRef.current?.setCurrentBoardId(board.Id);
    handleClearTemplate();
  };

  const onTaskTypeChange = async (taskType) => {
    const isTaxReturn = taskType.Type === Enums.TaskType.TaxReturn;

    dueDateRuleRef.current?.setDueDateRuleInfo(null);
    dueDateRef.current?.setHasDueDateInfo(false);
    startDateRef.current?.setHasDueDateInfo(false);
    descriptionRef.current.setValue("");
    tagsRef.current.setValue([]);
    dueDateRef.current?.setCalculatedDate(null);
    startDateRef.current?.setCalculatedDate(null);
    selectedTemplateRef.current?.setCurrentTemplate(null);

    taxYearRef?.current?.setHasTaskTaxReturnInfo(isTaxReturn);
    agencyRef?.current?.setHasTaskTaxReturnInfo(isTaxReturn);
    periodEndRef?.current?.setShowComponent(isTaxReturn);
    periodEndRef.current?.setHasDueDateInfo(false);
    taxDeadlineRef?.current?.setShowComponent(isTaxReturn);
    taxDeadlineRef.current?.setHasDueDateInfo(false);

    autoRolloverComponentRef?.current?.setShowContainer(taskType.AutoRollover);
    templateRef.current?.setTaskType(taskType?.Id);
    taskCustomFieldsRef.current?.setTaskTypeId(taskType?.Id);
  };

  const onModelBeforeClose = () => {};

  const closeModal = () => {
    clearValidationErrors();
    setPersistentData({});
    setShowModal(false);
  };

  const openModal = () => {
    setShowModal(true);
  };

  const toggleModal = () => {
    if (showModal) {
      closeModal();
    } else {
      openModal();
    }
  };

  const onOpen = () => {
    if (taskTypeRef != null) {
      autoRolloverComponentRef?.current?.setShowContainer(
        taskTypeRef?.current?.getValue()?.AutoRollover
      );
      taskCustomFieldsRef.current?.setTaskTypeId(
        taskTypeRef?.current?.getValue()?.Id
      );
    }
  };

  const getTaxYearId = (isPrevious) => {
    const currentYear = new Date().getFullYear();
    let year = currentYear;

    if (isPrevious) {
      year = currentYear - 1;
    }

    const result = taxYears?.find((item) => parseInt(item.Year) === year);
    return result ? result?.Id : null; // returns the Id or null if not found
  };

  const ModalContent = () => {
    const onTaskTemplateChange = async (e) => {
      // This is to clear the validation errors when the clear button is selected
      if (!e.itemData) {
        handleClearTemplate();
      }

      if (isGuid(e.itemData?.Id)) {
        const template = e.itemData;
        const taskType = taskTypeRef.current.getValue();

        selectedTemplateRef.current?.setCurrentTemplate(template);

        dueDateRef.current?.setHasDueDateInfo(
          template?.HasDueDateInfo ?? false
        );

        dueDateRuleRef.current?.setDueDateRuleInfo(
          template?.HasDueDateInfo ? template?.DueDateInfo : null
        );

        startDateRef.current?.setHasDueDateInfo(
          template?.HasDueDateInfo ?? false
        );

        taxDeadlineRef.current?.setHasDueDateInfo(
          template?.HasDueDateInfo ?? false
        );

        periodEndRef.current?.setHasDueDateInfo(
          template?.HasDueDateInfo ?? false
        );

        descriptionRef.current.setValue(e.itemData?.Description ?? "");
        tagsRef.current.setValue(e.itemData?.Tags ?? []);
        agencyRef.current?.setValue(e.itemData?.Agency ?? null);

        if (
          template?.DueDateInfo?.DueDateYearCalculation != null &&
          taskType?.Type === Enums.TaskType.TaxReturn
        ) {
          const isPreviousTy =
            template?.DueDateInfo?.DueDateYearCalculation ===
            Enums.DueDateYearCalculationTypes.PeriodEnd;

          const ty = getTaxYearId(isPreviousTy);
          taxYearRef.current?.setValue?.(ty);
        }

        if (
          e.itemData?.DueDateInfo?.DateFrequency !== Enums.DateFrequency.Monthly
        )
          await setDueDateByDueDateRule(
            template,
            clientRef.current?.getValue(),
            true
          );

        if (
          e.itemData?.DueDateInfo?.DateFrequency !== Enums.DateFrequency.Annual
        ) {
          dueDateRef.current.setCalculatedDate(null);
          startDateRef.current.setCalculatedDate(null, true);

          if (taskType?.Key === "TAX") {
            periodEndRef.current.setCalculatedDate(null);
            taxDeadlineRef.current.setCalculatedDate(null);
          }
        }
      }
    };

    const onTaxYearChange = async (e) => {
      const template = selectedTemplateRef.current?.getCurrentTemplate();
      if (e.isInteracted && template) {
        await setDueDateByDueDateRule(template, clientRef.current?.getValue());
      }
    };

    const onClientChange = async (e) => {
      const template = selectedTemplateRef.current?.getCurrentTemplate();
      if (template) {
        await setDueDateByDueDateRule(template, e.itemData);
      }
    };

    const onCheckBoxChange = (e) => {
      if (e.checked) {
        assigneeRef.current.setValue(UserId);
      } else assigneeRef.current.setValue("");
    };

    const onAssigneeChange = (e) => {
      if (e.value != UserId && assigneeCheckBoxRef.current.getValue())
        assigneeCheckBoxRef.current.setValue(false);
    };

    const setDueDateByDueDateRule = async (
      taskTemplate,
      crmClient = null,
      overwrite = false
    ) => {
      try {
        //Avoid the user from creating a task using an outdated date by accident
        ongoingCalculateRequest.current++;
        createSplitButtonRef.current.disabled = true;
        dueDateRuleRef.current?.setDueDateCalculation({});

        const taskType = taskTypeRef.current.getValue();
        const isTaxReturn = taskType?.Key === "TAX";

        const dueDate = {
          TemplateId: taskTemplate?.Id,
          ClientId: crmClient?.Id,
          Year: taxYearRef.current?.getValue?.() ?? new Date().getFullYear()
        };

        const dateResponse = await calculateDueDate(dueDate);

        if (
          taskTemplate.DueDateInfo?.DateFrequency === Enums.DateFrequency.Annual
        ) {
          startDateRef.current.setHasDueDateInfo(false);
          startDateRef.current.setCalculatedDate(null);

          dueDateRef.current.setHasDueDateInfo(false);
          dueDateRef.current.setCalculatedDate(
            moment(dateResponse.data?.Annually).toDate(),
            overwrite
          );

          if (isTaxReturn) {
            taxDeadlineRef.current.setHasDueDateInfo(false);
            periodEndRef.current.setHasDueDateInfo(false);

            taxDeadlineRef.current.setCalculatedDate(
              moment(dateResponse.data?.Annually).toDate(),
              overwrite
            );
            periodEndRef.current.setCalculatedDate(
              moment(dateResponse.data?.PeriodEnd).toDate(),
              overwrite
            );
          }
        } else {
          dueDateRuleRef.current?.setDueDateCalculation(dateResponse.data);
        }
      } catch (e) {
        console.error(e);
        toast.error(t("ErrorMsg"), { position: "bottom-left" });
      } finally {
        ongoingCalculateRequest.current--;

        if (ongoingCalculateRequest.current === 0) {
          createSplitButtonRef.current.disabled = false;
        }
      }
    };

    const onCreateTask = (args, createAndOpen = false) => {
      sessionStorage.setItem("create-and-open", createAndOpen);

      if (
        checkDueDateRuleCheckboxes(
          dueDateRuleRef?.current?.getDueDateRuleInfo()?.DateFrequency,
          dueDateRuleRef?.current?.getCheckedValues()
        )
      ) {
        let postData = {};

        const boardValue = boardRef.current.getValue()?.Id;
        const taskTypeValue = taskTypeRef.current.getValue()?.Id;
        const template = selectedTemplateRef.current?.getCurrentTemplate();
        const stageValue = stageRef.current.getValue()?.Id;
        const nameValue = templateRef.current.getValue()?.Name;
        const descriptionValue = descriptionRef.current.getValue();
        const clientValue = clientRef.current.getValue()?.Id;
        const assigneeValue = assigneeRef.current.getValue();
        const duedateValue = dueDateRef.current.getValue() ?? null;
        const startDateValue = startDateRef.current.getValue() ?? null;
        const collaboratorValue = collaboratorRef.current.getValue() ?? [];
        const autoRolloverValue = autoRolloverComponentRef.current?.getValue();
        const tagValues = tagsRef.current
          ?.getValue()
          ?.map?.((x) => ({ Title: x.Title }));

        postData = {
          BoardId: boardValue,
          TaskTypeId: taskTypeValue,
          TemplateId: template?.Id,
          Template: template,
          StageId: stageValue,
          TaskName: nameValue,
          Description: descriptionValue,
          ClientId: clientValue,
          AssigneeId: assigneeValue,
          DueDate: duedateValue,
          StartDate: startDateValue,
          Tags: tagValues,
          HasTaskTaxReturnInfo: taxYearRef?.current?.getValue?.()
            ? true
            : false,
          AutoRollover: autoRolloverValue,
          Collaborators: collaboratorValue.map((id) => ({
            Id: id
          })),
          CurrentUserId: UserId,
          DueDateRules: dueDateRuleRef.current.getCheckedValues(),
          HasDueDateInfo: template?.HasDueDateInfo ?? false
        };

        if (taxYearRef?.current?.getValue?.()) {
          postData = {
            ...postData,
            TaskTaxReturnInfo: {
              TaxYear: taxYearRef.current.getValue() ?? null,
              FilingStatus: 2,
              FormCode: template?.Name ?? null,
              TaxDeadline: taxDeadlineRef.current?.getValue(),
              PeriodEnd: periodEndRef.current?.getValue(),
              Agency: formatAgency(agencyRef.current?.getValue())
            }
          };
        }

        const customFieldValues =
          taskCustomFieldsRef?.current?.getCustomFieldValues();
        if (customFieldValues?.length > 0) {
          postData = {
            ...postData,
            TaskCustomFields: customFieldValues
          };
        }

        setPersistentData({
          ...postData,
          DueDateRuleInfo: template?.DueDateInfo ?? null
        });

        createTask(
          { task: postData },
          {
            onSuccess: (response) => {
              closeModal();

              toast.success(t("Success"), { position: "bottom-left" });
              toggleEvent(Events.RefreshTaskGrid);

              if (createAndOpen && response?.Id) {
                toggleEvent(Events.TaskOverlay, {
                  taskId: response.Id
                });
              }
            },
            onError: (errors) => {
              var formErrors = errors?.data;
              Object.keys(formErrors.errors).forEach((key) => {
                formErrors.errors[key] = formErrors.errors[key].map(
                  (message) => {
                    return message.replace("Id", "");
                  }
                );
              });
              setValidationErrors(formErrors);
            }
          }
        );
      } else {
        dueDateRuleRef.current.setShowError(true);
      }
    };

    const checkDueDateRuleCheckboxes = (dataFrequency, checkboxes) => {
      switch (dataFrequency) {
        case 1:
          return Object.values(checkboxes.quarterly).some(
            (val) => val === true
          );
        case 2:
          return Object.values(checkboxes.monthly).some((val) => val === true);
        default:
          return true;
      }
    };

    const createTaskBtnItems = [
      {
        text: isTruthyString(createAndOpenBtn)
          ? t("CreateTask")
          : t("CreateAndOpen"),
        iconCss: "",
        onClick: async (e) => {
          if (isTruthyString(createAndOpenBtn)) onCreateTask(e, false);
          else onCreateTask(e, true);
        }
      }
    ];

    const onSelectDropDownTaskMenu = (args) => {
      args?.item?.onClick?.();
    };

    return (
      <div className="et-create-task-modal">
        <div className="row et-bb-light-gray-1">
          <div className="col-8">
            <div className="my-3 mx-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <Trans components={{ b: <span className="et-red" /> }}>
                    {t("RequiredNote")}
                  </Trans>
                </div>
                <div>
                  <SelectedTemplate
                    ref={selectedTemplateRef}
                    onClearTemplate={handleClearTemplate}
                    persistentData={persistentData?.Template}
                  />
                </div>
              </div>
            </div>

            <div className="my-3 mx-4">
              <ClientDropDown
                id={ids.client}
                initialValue={persistentData?.ClientId}
                ref={clientRef}
                onChange={onClientChange}
              />
            </div>

            <div className="my-3 mx-4">
              <TaskTemplateDropDown
                id={ids.template}
                initialTaskType={
                  persistentData?.TaskTypeId ??
                  taskTypeRef?.current?.getValue()?.Id
                }
                persistentData={
                  persistentData?.TemplateId ?? persistentData?.TaskName
                }
                ref={templateRef}
                change={onTaskTemplateChange}
              />
            </div>

            <div className="my-3 mx-4 d-flex justify-content-start gap-3">
              <AgencyCombobox
                id={ids.agency}
                initialTaskType={taskTypeRef.current?.getValue()}
                ref={agencyRef}
                dataSource={agencies ?? []}
                value={persistentData?.TaskTaxReturnInfo?.Agency}
              />
              <TaxYearDropDown
                id={ids.taxYear}
                initialValue={persistentData?.TaskTaxReturnInfo?.TaxYear}
                ref={taxYearRef}
                initialTaskType={taskTypeRef.current?.getValue()}
                onChange={onTaxYearChange}
              />
              <DueDatePicker
                id={ids.periodEnd}
                name={"TaskTaxReturnInfo.PeriodEnd"}
                labelText={
                  <Trans components={{ b: <span className="et-red" /> }}>
                    {t("PeriodEndLabel")}
                  </Trans>
                }
                initialValue={persistentData?.TaskTaxReturnInfo?.PeriodEnd}
                ref={periodEndRef}
                show={
                  persistentData?.HasTaskTaxReturnInfo ??
                  taskTypeRef.current?.getValue()?.Type ===
                    Enums.TaskType.TaxReturn
                }
              />
              <DueDatePicker
                id={ids.taxDeadline}
                name={"TaskTaxReturnInfo.TaxDeadline"}
                labelText={
                  <Trans components={{ b: <span className="et-red" /> }}>
                    {t("TaxDeadlineLabel")}
                  </Trans>
                }
                show={
                  persistentData?.HasTaskTaxReturnInfo ??
                  taskTypeRef.current?.getValue()?.Type ===
                    Enums.TaskType.TaxReturn
                }
                initialValue={persistentData?.TaskTaxReturnInfo?.TaxDeadline}
                ref={taxDeadlineRef}
              />
            </div>

            <div className="my-3 mx-4">
              <DueDateRule
                id={ids.dueDateRule}
                initialValue={{
                  TaXYear: persistentData?.TaskTaxReturnInfo?.TaxYear,
                  CheckedValues: persistentData?.DueDateRules,
                  DueDateRule: persistentData?.DueDateRuleInfo
                }}
                ref={dueDateRuleRef}
              />
            </div>

            <div className="my-3 mx-4">
              <TaskDescriptionTextBox
                labelText={t("DescriptionLabel")}
                id={ids.description}
                initialValue={persistentData?.Description}
                ref={descriptionRef}
              />
            </div>

            <div className="my-3 mx-4 pb-3">
              <div className="create-task-multiselect">
                <TagMultiSelect
                  id={ids.tags}
                  initialValue={persistentData?.Tags}
                  ref={tagsRef}
                />
              </div>
            </div>

            <div className="">
              <TaskCustomFields
                ref={taskCustomFieldsRef}
                initialValue={persistentData}
              />
            </div>
          </div>
          <div className="col-4 et-bl-light-gray-1">
            <div className="my-3 mx-4 mx-3">
              <StageDropdown
                id={ids.stage}
                ref={stageRef}
                boardId={persistentData?.BoardId ?? currentBoardId}
              />
            </div>
            <div className="my-3 mx-4 mx-3">
              <DueDatePicker
                id={ids.startDate}
                name={"StartDate"}
                labelText={t("StartDateLabel")}
                initialValue={persistentData?.StartDate}
                readonly={
                  taskTypeRef.current?.getValue()?.Type !==
                    Enums.TaskType.Custom &&
                  dueDateRuleRef?.current?.hasDueDateRuleInfo()
                }
                ref={startDateRef}
              />
            </div>
            <div className="my-3 mx-4 mx-3">
              <DueDatePicker
                id={ids.dueDate}
                name={"DueDate"}
                labelText={
                  <Trans components={{ b: <span className="et-red" /> }}>
                    {t("DueDateLabel")}
                  </Trans>
                }
                initialValue={persistentData?.DueDate}
                ref={dueDateRef}
                readonly={
                  taskTypeRef.current?.getValue()?.Type !==
                    Enums.TaskType.Custom &&
                  dueDateRuleRef?.current?.hasDueDateRuleInfo() &&
                  !persistentData?.DueDate
                }
              />
            </div>
            <div className="my-3 mx-4 mx-3">
              <AssigneeDropDown
                id={ids.assignee}
                initialValue={persistentData?.AssigneeId}
                ref={assigneeRef}
                boardPermissions={boardPermissions}
                change={onAssigneeChange}
              />
              <div className="my-1">
                <AssigneeCheckBox
                  id={ids.assignToMe}
                  initialValue={
                    persistentData?.AssigneeId === persistentData?.CurrentUserId
                  }
                  ref={assigneeCheckBoxRef}
                  change={onCheckBoxChange}
                />
              </div>
            </div>
            <div className="my-3 mx-4 mx-3">
              <div className="create-task-multiselect">
                <CollaboratorMultiSelect
                  id={ids.collaborators}
                  initialValue={persistentData?.Collaborators?.map?.(
                    (collaborator) => collaborator.Id
                  )}
                  ref={collaboratorRef}
                  boardPermissions={boardPermissions?.Entities}
                  label={t("CollaboratorsLabel")}
                />
              </div>
            </div>

            <AutoRolloverContainer
              ref={autoRolloverComponentRef}
              persistentData={persistentData}
              show={taskTypeRef?.current?.getValue()?.AutoRollover}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end align-items-center create-task-modal-btns mx-auto px-4 gap-3">
          <Button
            text={t("Cancel")}
            className="btn-tertiary"
            onClick={() => modalRef.current.close()}
          />
          <SplitButton
            ref={createSplitButtonRef}
            id={ids.createButton}
            items={createTaskBtnItems}
            content={
              isTruthyString(createAndOpenBtn)
                ? t("CreateAndOpen")
                : t("CreateTask")
            }
            className="btn btn-primary"
            onClick={(e) => {
              if (isTruthyString(createAndOpenBtn)) onCreateTask(e, true);
              else onCreateTask(e, false);
            }}
            select={onSelectDropDownTaskMenu}
            loading={isPending}
          />
        </div>
      </div>
    );
  };

  useRegisterEvent(ModalEvents.CreateTaskModal, toggleModal);

  return ReactDOM.createPortal(
    <Modal
      id={ModalEvents.CreateTaskModal.Id}
      ref={modalRef}
      isModal
      showCloseIcon={true}
      visible={showModal}
      content={<ModalContent />}
      close={closeModal}
      beforeClose={onModelBeforeClose}
      open={onOpen}
      minHeight="20hv"
      width="1140px"
      customHeader={headerTemplate}
    ></Modal>,
    document.body
  );
};
